import { stopEvent } from '@pkg/shared/utils/ui.utils'
import type React from 'react'
import { FaUndo } from 'react-icons/fa'
import { FaPenToSquare } from 'react-icons/fa6'
import { twMerge } from 'tailwind-merge'
import { useEditableFormState, useEditableFormStateOptional } from '../contexts/editableForm'

export type EditableLabelProps = {
  mode: 'read' | 'edit'
  enterEdit: () => void
  exitRead: () => void
  onUndo: () => void
  htmlFor?: string
  className?: string
  children?: React.ReactNode
  canUndo?: boolean
  showActions?: boolean
}

// Inline label that shows an edit (read mode) or undo (edit mode) icon.
// The icon reacts to the parent EditableShell hover via Tailwind's `group-hover`.
export function EditableLabel(props: EditableLabelProps) {
  const { mode, onUndo, htmlFor, canUndo, className, children, showActions = true } = props
  const forceMode = useEditableFormStateOptional(s => s.policy.forceMode)
  const hideActions = forceMode === 'read' || !showActions
  const canEnterEdit = forceMode !== 'read' || !showActions
  const showEditIcon = !hideActions && mode === 'read' && canEnterEdit
  const showUndoButton = !hideActions && mode === 'edit' && !!canUndo
  const baseIconClasses =
    'ml-2 inline-flex items-center align-middle text-gray-400 group-hover:text-primary hover:text-primary transition-colors'
  const Label = htmlFor ? 'label' : 'span'

  return (
    <span className={twMerge('inline-flex items-center', className)}>
      <Label className="inline-block" htmlFor={htmlFor}>
        {children}
      </Label>
      {showEditIcon ? (
        // Decorative pencil; the surrounding EditableShell is the actual button
        // in read mode (nesting two real buttons is HTML-invalid).
        <span aria-hidden="true" className={twMerge(baseIconClasses, 'pointer-events-none')}>
          <FaPenToSquare className="size-3" />
        </span>
      ) : showUndoButton ? (
        <button
          type="button"
          aria-label="Zurücksetzen"
          className={twMerge(baseIconClasses, 'cursor-pointer')}
          onClick={stopEvent(() => onUndo())}
        >
          <FaUndo className="size-3" />
        </button>
      ) : null}
    </span>
  )
}
